import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  createAvailabilitySchedule, updateAvailabilitySchedule,
  createAdHocAvailabilitySchedule, updateAdHocAvailabilitySchedule,
} from "api";
import { Modal, Spinner, Select } from "components/common";
import type { DayOfWeek, AvailabilitySchedule, AdHocAvailabilitySchedule } from "types";
import { formatSlotTime, slotToInput, inputToSlot, SCHEDULE_TIME_ZONE_LABEL } from "utils/scheduleTime";

export const ALL_DAYS: DayOfWeek[] = [
  "monday", "tuesday", "wednesday", "thursday",
  "friday", "saturday", "sunday",
];

// ─── Availability Display ─────────────────────────────────────────────────────

export function AvailabilityDisplay({
  availability,
}: {
  availability: Record<string, Array<{ start: string; end: string }>>;
}) {
  const activeDays = ALL_DAYS.filter((d) => availability[d] && availability[d].length > 0);

  if (activeDays.length === 0) {
    return <p className="text-sm text-gray-400">No availability set.</p>;
  }

  return (
    <div className="space-y-2">
      {activeDays.map((day) => (
        <div key={day} className="flex gap-3 text-sm">
          <span className="w-24 capitalize text-gray-500 shrink-0">{day}</span>
          <div className="flex flex-wrap gap-2">
            {availability[day].map((slot, i) => (
              <span key={i} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                {formatSlotTime(slot.start)} – {formatSlotTime(slot.end)}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Availability Modal ───────────────────────────────────────────────────────

interface SlotState { start: string; end: string }
type DaySlots = Record<DayOfWeek, SlotState[]>;

export function AvailabilityModal({
  userId,
  existing,
  onClose,
  onSuccess,
}: {
  userId:    number;
  existing:  AvailabilitySchedule | null;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const isEditing = !!existing;

  const initDays = (): DaySlots => {
    const result: DaySlots = {
      monday: [], tuesday: [], wednesday: [], thursday: [],
      friday: [], saturday: [], sunday: [],
    };
    if (existing?.availability) {
      for (const day of ALL_DAYS) {
        const slots = existing.availability[day];
        if (slots && slots.length > 0) {
          result[day] = slots.map((s) => ({ start: slotToInput(s.start), end: slotToInput(s.end) }));
        }
      }
    }
    return result;
  };

  const [days, setDays] = useState<DaySlots>(initDays);

  const addSlot    = (day: DayOfWeek) =>
    setDays((prev) => ({ ...prev, [day]: [...prev[day], { start: "", end: "" }] }));

  const updateSlot = (day: DayOfWeek, index: number, field: "start" | "end", value: string) =>
    setDays((prev) => ({
      ...prev,
      [day]: prev[day].map((s, i) => i === index ? { ...s, [field]: value } : s),
    }));

  const removeSlot = (day: DayOfWeek, index: number) =>
    setDays((prev) => ({ ...prev, [day]: prev[day].filter((_, i) => i !== index) }));

  const buildPayload = () => {
    const result: Record<string, Array<{ start: string; end: string }>> = {};
    for (const day of ALL_DAYS) {
      const valid = days[day].filter((s) => s.start && s.end);
      if (valid.length > 0) {
        result[day] = valid.map((s) => ({ start: inputToSlot(s.start), end: inputToSlot(s.end) }));
      }
    }
    return result;
  };

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => {
      const availability = buildPayload();
      if (isEditing) {
        return updateAvailabilitySchedule(existing.id, { availability });
      }
      return createAvailabilitySchedule({ contact_user_id: userId, availability });
    },
    onSuccess,
  });

  const hasAtLeastOneSlot = ALL_DAYS.some((d) => days[d].some((s) => s.start && s.end));

  return (
    <Modal title={isEditing ? "Edit availability" : "Add availability"} onClose={onClose}>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        <p className="text-xs text-gray-400">Times in {SCHEDULE_TIME_ZONE_LABEL}</p>
        {ALL_DAYS.map((day) => (
          <div key={day}>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-600 capitalize">{day}</label>
              <button
                type="button"
                onClick={() => addSlot(day)}
                className="text-xs text-brand-600 hover:text-brand-700 transition-colors"
              >
                + Add slot
              </button>
            </div>
            {days[day].length === 0 ? (
              <p className="text-xs text-gray-300 mb-1">Unavailable</p>
            ) : (
              <div className="space-y-1.5">
                {days[day].map((slot, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="time"
                      className="input flex-1 text-sm"
                      value={slot.start}
                      onChange={(e) => updateSlot(day, i, "start", e.target.value)}
                    />
                    <span className="text-xs text-gray-400">to</span>
                    <input
                      type="time"
                      className="input flex-1 text-sm"
                      value={slot.end}
                      onChange={(e) => updateSlot(day, i, "end", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeSlot(day, i)}
                      className="text-gray-400 hover:text-red-500 text-sm transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-red-500 mt-3">Something went wrong.</p>}
      <div className="flex gap-3 pt-4">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!hasAtLeastOneSlot || isPending}
          onClick={() => mutate()}
        >
          {isPending ? <Spinner className="h-4" /> : isEditing ? "Save changes" : "Create schedule"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Ad-Hoc Availability Modal ────────────────────────────────────────────────

// Returns the Monday of the ISO week containing `date`, formatted YYYY-MM-DD.
function toMondayDateString(date: Date): string {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Produces an array of Monday YYYY-MM-DD strings for the next N weeks (including current).
function upcomingMondays(count = 12): string[] {
  const mondays: string[] = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i * 7);
    mondays.push(toMondayDateString(d));
  }
  // Deduplicate (current week may appear twice if today is Monday)
  return [...new Set(mondays)];
}

export function AdHocAvailabilityModal({
  userId,
  existing,
  onClose,
  onSuccess,
}: {
  userId:    number;
  existing:  AdHocAvailabilitySchedule | null;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const isEditing = !!existing;

  const mondays = upcomingMondays(12);
  const defaultMonday = existing?.week_start_date ?? mondays[0];
  const [weekStartDate, setWeekStartDate] = useState(defaultMonday);

  const initDays = (): DaySlots => {
    const result: DaySlots = {
      monday: [], tuesday: [], wednesday: [], thursday: [],
      friday: [], saturday: [], sunday: [],
    };
    if (existing?.availability) {
      for (const day of ALL_DAYS) {
        const slots = existing.availability[day];
        if (slots && slots.length > 0) {
          result[day] = slots.map((s) => ({ start: slotToInput(s.start), end: slotToInput(s.end) }));
        }
      }
    }
    return result;
  };

  const [days, setDays] = useState<DaySlots>(initDays);

  const addSlot    = (day: DayOfWeek) =>
    setDays((prev) => ({ ...prev, [day]: [...prev[day], { start: "", end: "" }] }));

  const updateSlot = (day: DayOfWeek, index: number, field: "start" | "end", value: string) =>
    setDays((prev) => ({
      ...prev,
      [day]: prev[day].map((s, i) => i === index ? { ...s, [field]: value } : s),
    }));

  const removeSlot = (day: DayOfWeek, index: number) =>
    setDays((prev) => ({ ...prev, [day]: prev[day].filter((_, i) => i !== index) }));

  const buildPayload = () => {
    const result: Record<string, Array<{ start: string; end: string }>> = {};
    for (const day of ALL_DAYS) {
      const valid = days[day].filter((s) => s.start && s.end);
      if (valid.length > 0) {
        result[day] = valid.map((s) => ({ start: inputToSlot(s.start), end: inputToSlot(s.end) }));
      }
    }
    return result;
  };

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => {
      const availability = buildPayload();
      if (isEditing) {
        return updateAdHocAvailabilitySchedule(existing.id, { availability });
      }
      return createAdHocAvailabilitySchedule({ contact_user_id: userId, week_start_date: weekStartDate, availability });
    },
    onSuccess,
  });

  const hasAtLeastOneSlot = ALL_DAYS.some((d) => days[d].some((s) => s.start && s.end));

  // Format a Monday date string as a human-readable week label
  const formatWeekLabel = (monday: string): string => {
    const d = new Date(`${monday}T00:00:00`);
    const end = new Date(d);
    end.setDate(d.getDate() + 6);
    return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} – ${end.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;
  };

  return (
    <Modal title={isEditing ? "Edit ad-hoc schedule" : "Add ad-hoc schedule"} onClose={onClose}>
      <div className="space-y-4">
        {/* Week picker */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Week</label>
          {isEditing ? (
            <p className="text-sm text-gray-700">{formatWeekLabel(existing.week_start_date)}</p>
          ) : (
            <Select
              size="sm"
              value={weekStartDate}
              onChange={(e) => setWeekStartDate(e.target.value)}
            >
              {mondays.map((m) => (
                <option key={m} value={m}>{formatWeekLabel(m)}</option>
              ))}
            </Select>
          )}
        </div>

        {/* Day/time slots */}
        <div className="space-y-4 max-h-[50vh] overflow-y-auto">
          <p className="text-xs text-gray-400">Times in {SCHEDULE_TIME_ZONE_LABEL}</p>
          {ALL_DAYS.map((day) => (
            <div key={day}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-600 capitalize">{day}</label>
                <button
                  type="button"
                  onClick={() => addSlot(day)}
                  className="text-xs text-brand-600 hover:text-brand-700 transition-colors"
                >
                  + Add slot
                </button>
              </div>
              {days[day].length === 0 ? (
                <p className="text-xs text-gray-300 mb-1">Unavailable</p>
              ) : (
                <div className="space-y-1.5">
                  {days[day].map((slot, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="time"
                        className="input flex-1 text-sm"
                        value={slot.start}
                        onChange={(e) => updateSlot(day, i, "start", e.target.value)}
                      />
                      <span className="text-xs text-gray-400">to</span>
                      <input
                        type="time"
                        className="input flex-1 text-sm"
                        value={slot.end}
                        onChange={(e) => updateSlot(day, i, "end", e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removeSlot(day, i)}
                        className="text-gray-400 hover:text-red-500 text-sm transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {error && <p className="text-sm text-red-500 mt-3">Something went wrong.</p>}
      <div className="flex gap-3 pt-4">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!hasAtLeastOneSlot || isPending}
          onClick={() => mutate()}
        >
          {isPending ? <Spinner className="h-4" /> : isEditing ? "Save changes" : "Create schedule"}
        </button>
      </div>
    </Modal>
  );
}
